import { useCallback, useEffect, useMemo, useState } from 'react';
import { isValidPhoneNumber, parsePhoneNumberFromString } from 'libphonenumber-js';
import { countries, type Country, getFlagImagePath } from '../countries';
import { detectCountryFromPhone, getLocaleCountryCode, parseAndFormatPhone } from '../StanPhoneInput.logic';
import type { PhoneChange } from '../StanPhoneInput.types';
import type { DropdownMenuOption, DropdownValue } from '@/components/core-components/types';
import styles from '../StanPhoneInput.module.css';

// Fetch country related to their locale country code + list popular country codes
const userLocale = navigator.language;
const countryCode = userLocale.split('-')[1];

// Find the object with the specified code
const updatedCountries: Country[] = [...countries];
const index = updatedCountries.findIndex(country => country.code === countryCode);

if (index !== -1) {
    const foundCountry = updatedCountries[index];
    if (foundCountry) {
        // Popular codes filter if our country is one of these then push
        const popularCountryCodes = ['AU', 'CA', 'GB', 'US'].filter(code => code !== foundCountry.code);
        popularCountryCodes.push(foundCountry.code);

        // Push all popular countries + the divider
        popularCountryCodes.forEach(code => {
            const country = updatedCountries.find(country => country.code === code);
            if (country) {
                updatedCountries.unshift(country);
            }
        });
    }
}

// Map for fast country lookups by code
const countriesByCode = new Map(countries.map(country => [country.code, country]));

/**
 * Helper function to get the correct flag image path for production builds
 * Uses relative path to ensure Vite processes the asset correctly
 */

// Hardcoded fallback country (United States) - always available as last resort
const HARDCODED_FALLBACK_COUNTRY: Country = {
    name: 'United States',
    image: getFlagImagePath('us'),
    label: 'US +1',
    code: 'US',
    dialCode: '+1',
};

// Fallback country if locale detection fails
const fallbackCountry: Country = countriesByCode.get('US') ?? countries[0] ?? HARDCODED_FALLBACK_COUNTRY;

// Build dropdown options once (static data)
const dropdownOptions: DropdownMenuOption[] = updatedCountries.map(country => ({
    label: `${country.name} ${country.dialCode}`,
    value: country.code,
    icon: country.image,
    className: styles.optionItem,
}));

type UseStanPhoneInputArgs = {
    value?: string;
    onChange: (value: PhoneChange) => void;
    error?: boolean;
    /** Override default country detection (ISO 3166-1 alpha-2 code) */
    defaultCountry?: string;
};

type UseStanPhoneInputReturn = {
    options: DropdownMenuOption[];
    dropdownValue: DropdownValue;
    countryDial: string;
    countryImage: string;
    inputValue: string;
    handleInputChange: (next: string) => void;
    handleCountrySelect: (value: DropdownValue) => void;
    shouldShowError: boolean;
};

/**
 * Hook for managing phone input state and validation
 * Handles country selection, phone formatting, and change events
 */
export function useStanPhoneInput({
    value,
    onChange,
    error,
    defaultCountry: defaultCountryProp,
}: UseStanPhoneInputArgs): UseStanPhoneInputReturn {
    // Detect user's locale country once
    const localeCountry = useMemo(() => getLocaleCountryCode(), []);

    // Determine default country: prop > locale > fallback
    const resolvedDefaultCountry = useMemo(() => {
        // If prop is provided and valid, use it
        if (defaultCountryProp) {
            const propCountry = countriesByCode.get(defaultCountryProp.toUpperCase());
            if (propCountry) return propCountry;
        }
        // Otherwise fall back to locale detection
        if (localeCountry) {
            const localeCountryObj = countriesByCode.get(localeCountry);
            if (localeCountryObj) return localeCountryObj;
        }
        return fallbackCountry;
    }, [defaultCountryProp, localeCountry]);

    // State
    const [countryCode, setCountryCode] = useState<string>(resolvedDefaultCountry.code);

    // Update country when defaultCountry prop changes (e.g., when IP detection completes)
    useEffect(() => {
        if (defaultCountryProp) {
            const propCountry = countriesByCode.get(defaultCountryProp.toUpperCase());
            if (propCountry) {
                setCountryCode(propCountry.code);
            }
        }
    }, [defaultCountryProp]);
    const [inputValue, setInputValue] = useState<string>('');

    // Get selected country data
    const selectedCountry = useMemo(
        () => countriesByCode.get(countryCode) ?? resolvedDefaultCountry,
        [countryCode, resolvedDefaultCountry]
    );

    // Build dropdown value from selected country
    const dropdownValue = useMemo<DropdownValue>(
        () => ({
            label: `${selectedCountry.code} ${selectedCountry.dialCode}`,
            value: selectedCountry.code,
            image: selectedCountry.image,
        }),
        [selectedCountry]
    );

    // Sync with external value changes
    useEffect(() => {
        if (!value) {
            setInputValue('');
            return;
        }

        const isValid = isValidPhoneNumber(value);
        if (!isValid) return;
        const parsed = parsePhoneNumberFromString(value);
        if (!parsed) return;

        setInputValue(parsed.formatNational());
        if (parsed.country) {
            const found = countriesByCode.get(parsed.country);
            if (found) {
                setCountryCode(found.code);
            }
        }
    }, [value]);

    /**
     * Emits a phone change event with parsed and formatted data
     * Also updates country code if phone number indicates different country
     */
    const emitChange = useCallback(
        (raw: string, cCode: string) => {
            const phoneChange = parseAndFormatPhone(raw, cCode);
            onChange(phoneChange);

            // Auto-detect and switch country if phone indicates different country
            if (phoneChange.country && phoneChange.country !== cCode && raw.trim().length > 0) {
                const detectedCountry = detectCountryFromPhone(raw);
                if (detectedCountry) {
                    const found = countriesByCode.get(detectedCountry);
                    if (found) {
                        setCountryCode(found.code);
                    }
                }
            }
        },
        [onChange]
    );

    /**
     * Handles input field changes
     */
    const handleInputChange = useCallback(
        (next: string) => {
            setInputValue(next);
            emitChange(next, countryCode);
        },
        [emitChange, countryCode]
    );

    /**
     * Handles country dropdown selection
     */
    const handleCountrySelect = useCallback(
        (val: DropdownValue) => {
            const found = countriesByCode.get(val.value);
            if (!found) return;
            setCountryCode(found.code);
            emitChange(inputValue, found.code);
        },
        [emitChange, inputValue]
    );

    // Show error if explicitly set via prop
    const shouldShowError = Boolean(error);

    return {
        options: dropdownOptions,
        dropdownValue,
        countryDial: selectedCountry.dialCode,
        countryImage: selectedCountry.image,
        inputValue,
        handleInputChange,
        handleCountrySelect,
        shouldShowError,
    };
}
